import React, { useState, useEffect } from 'react';
import { App, Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api';
import { formatDate } from '../utils/format';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';

interface ServiceProvider {
  id: string;
  name: string;
  logo?: string;
  tin?: string;
  phone?: string;
  admins?: Array<{ email: string; first_name?: string; last_name?: string }>;
  created_at: string;
}

const ServiceProviders: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [serviceProviders, setServiceProviders] = useState<ServiceProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    loadServiceProviders();
  }, [page, pageSize]);

  const loadServiceProviders = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/admin/service-providers', {
        params: {
          offset: (page - 1) * pageSize,
          limit: pageSize,
        },
      });
      setServiceProviders(response.data.service_providers || []);
      setTotal(response.data.count || 0);
    } catch (error: any) {
      message.error(error.response?.data?.message || t('serviceProviders.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredData = debouncedSearch
    ? serviceProviders.filter(
        (sp) =>
          sp.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : serviceProviders;

  const columns = [
    {
      title: t('serviceProviders.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('serviceProviders.email'),
      key: 'admin_emails',
      render: (_: any, record: ServiceProvider) =>
        record.admins?.map((a) => a.email).join(', ') || '-',
    },
    {
      title: t('common.createdAt'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => formatDate(date, i18n.language),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontSize: 24, fontWeight: 600, color: '#334E3B', margin: 0 }}>
          {t('serviceProviders.title')}
        </h1>
        <Input
          placeholder={t('common.search')}
          prefix={<SearchOutlined />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        onRow={(record) => ({
          onClick: () => navigate(`/service-providers/${record.id}`),
          style: { cursor: 'pointer' },
        })}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />
    </div>
  );
};

export default ServiceProviders;
